import { FC } from 'react';

import Modal from '../Modal';

import './ConfirmationModal.scss';

import { MessageType, TestMetadata } from '../types';
import Typography from '../Typography';

export interface ConfirmationModalProps {
  /** Set text for modal header */
  title: string;
  /** Set text for modal content */
  text: string;
  /** Cancel Button text */
  cancelButtonText?: string;
  /** Accept Button text */
  acceptButtonText?: string;
  /** Callback fired when the component requests to be closed */
  onClose?: () => void;
  /** Callback fired when the component requests to be confirm */
  onConfirm?: () => void;
  /** Modal type */
  messageType?: MessageType;
  /** Modal should overlay */
  bodyOverlay?: 'blackout' | 'whiteout';
  /** Hide/show the close button */
  hideClose?: boolean;
  /** Show smaller buttons */
  smallButtons?: boolean;
  /** Props to pass to the secondary button */
  secondaryButtonProps?: Record<string, unknown>;
  /** Props to pass to the primary button */
  primaryButtonProps?: Record<string, unknown>;
  /** Test IDs for sections of ConfirmationModal */
  testMetadata?: {
    confirmationModal?: TestMetadata;
    primaryButton?: TestMetadata;
    secondaryButton?: TestMetadata;
    content?: TestMetadata;
  };
}

const ConfirmationModal: FC<ConfirmationModalProps> = ({
  title,
  text,
  cancelButtonText = 'No',
  acceptButtonText = 'Yes',
  onClose,
  onConfirm,
  messageType,
  testMetadata = {
    primaryButton: { 'data-testid': 'accept-modal-btn' },
  },
  bodyOverlay,
  hideClose,
  smallButtons,
  secondaryButtonProps = {},
  primaryButtonProps = {},
}) => {
  return (
    <Modal
      headerText={title}
      headerIconType={messageType}
      onClose={onClose}
      primaryButtonAction={onConfirm}
      secondaryButtonAction={onClose}
      primaryButton={acceptButtonText}
      secondaryButton={cancelButtonText}
      hideClose={hideClose}
      smallButtons={smallButtons}
      headerAlign="space-between"
      footerAlign="right"
      dialogClassName="sb-confirmation-modal__dialog"
      bodyOverlay={bodyOverlay}
      primaryButtonProps={{
        className: 'sb-confirmation-modal-button',
        ...primaryButtonProps,
        ...testMetadata?.primaryButton,
      }}
      secondaryButtonProps={{
        className: 'sb-confirmation-modal-button',
        ...secondaryButtonProps,
        ...testMetadata?.secondaryButton,
      }}
      {...testMetadata?.confirmationModal}
    >
      <Typography
        className="sb-confirmation-modal__content"
        variant="T20"
        {...testMetadata?.content}
      >
        {text}
      </Typography>
    </Modal>
  );
};

export default ConfirmationModal;
